import type { PlatformAppEntry } from "@av/apps/common-apps/platform/services/apps/types";
import { adminSettingsPermissions } from "../../../routes/useAdminSettingsNavigation";
import { useFetchPermissions } from "../../../services/admin-settings/admin-settings";

import type { ProductCode } from "../../../services/admin-settings/type";

export const adminSettingsProductPaths = {
  ats: "recruitment",
  hr: "hr",
  payroll: "payroll",
} as const;

const adminSettingsProductCodes: ProductCode[] = ["hr", "ats"];

type UseAdminSettingsAccessArgs = {
  apps: PlatformAppEntry[];
  currentProductCode: ProductCode | undefined;
  isAdminSettingsOpen: boolean;
  isCompanyKnowledgeEnabled: boolean;
  selectedSection: string;
};

export function resolveAdminSettingsProductCode(pathname: string): ProductCode | undefined {
  const pathSegments = pathname.split("/").filter(Boolean);

  if (pathSegments.includes(adminSettingsProductPaths.hr)) {
    return "hr";
  }

  if (pathSegments.includes(adminSettingsProductPaths.ats)) {
    return "ats";
  }

  return undefined;
}

export function useAdminSettingsAccess(args: UseAdminSettingsAccessArgs) {
  const {
    apps,
    currentProductCode,
    isAdminSettingsOpen,
    isCompanyKnowledgeEnabled,
    selectedSection,
  } = args;

  const hrPermissions = useFetchPermissions("hr", isAdminSettingsOpen);
  const atsPermissions = useFetchPermissions("ats", isAdminSettingsOpen);

  const isPermissionsLoading =
    hrPermissions.isGlobalPermissionsLoading || atsPermissions.isGlobalPermissionsLoading;

  const canManageHrRolesAndPermissions = hrPermissions.hasPermission(
    adminSettingsPermissions.rolesAndPermissions
  );
  const canManageAtsRolesAndPermissions = atsPermissions.hasPermission(
    adminSettingsPermissions.rolesAndPermissions
  );

  const productPermissions: Record<ProductCode, boolean> = {
    hr: canManageHrRolesAndPermissions,
    ats: canManageAtsRolesAndPermissions,
  };

  const canManageCurrentProductRolesAndPermissions = currentProductCode
    ? productPermissions[currentProductCode]
    : false;

  const canManageHrCompanyKnowledge =
    isCompanyKnowledgeEnabled &&
    hrPermissions.hasPermission(adminSettingsPermissions.companyKnowledgeView) &&
    hrPermissions.hasPermission(adminSettingsPermissions.companyKnowledgeEdit);

  const canManageCompanyKnowledge = currentProductCode === "hr" && canManageHrCompanyKnowledge;

  const adminPermissionPaths = new Set<string>(
    adminSettingsProductCodes
      .filter((productCode) => productPermissions[productCode])
      .map((productCode) => adminSettingsProductPaths[productCode])
  );

  const adminPermittedApps = apps.filter((app) => adminPermissionPaths.has(app.path));

  const isSelectedSectionAllowed =
    selectedSection === "products" ||
    adminPermittedApps.some((app) => app.path === selectedSection);
  const resolvedSelectedSection = isSelectedSectionAllowed ? selectedSection : "products";

  return {
    canManageAtsRolesAndPermissions,
    canManageCompanyKnowledge,
    canManageCurrentProductRolesAndPermissions,
    isPermissionsLoading,
    adminPermittedApps,
    resolvedSelectedSection,
    productPermissions,
  };
}
